import {Button, DataStatus, Dropdown, DropdownItem, openDialog, Tooltip} from 'platform/components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {SecondaryRecordId} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useEmailConfig, useKonzultaSmsAvailability} from '@dms/shared';
import {SendCustomerEmailForm, SendCustomerSmsForm} from '@dms/shared-aftersales';

import {Nullish, useBoolean} from 'shared/web';

interface ActivitySidebarSendMessageProps {
  customerId: string | Nullish;
  resourceId?: EntityResourceIds;
  recordId?: string;
  secondaryRecordIds?: SecondaryRecordId[];
  vehicleId?: string;
  defaultEmailAddress?: string | Nullish;
  defaultPhoneNumber?: string | Nullish;
}

export function ActivitySidebarSendMessage(props: ActivitySidebarSendMessageProps) {
  const [, setOpen, setClosed] = useBoolean();

  const {
    isEnabled: isEmailConfigEnabled,
    isLoading: isEmailConfigLoading,
    isError: isEmailConfigErrored,
  } = useEmailConfig();

  const {
    isSmsActionDisabled,
    smsTooltipLabel,
    isLoading: isKonzultaSmsLoading,
    isError: isKonzultaSmsError,
  } = useKonzultaSmsAvailability();

  const isLoading = isEmailConfigLoading || isKonzultaSmsLoading;
  const isError = isEmailConfigErrored || isKonzultaSmsError;

  if (isNilOrEmpty(props.customerId)) {
    return null;
  }

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Dropdown
        onOpen={setOpen}
        onClose={setClosed}
        closeOnBlur
        closeOnSelect
        dropdownControl={
          <Button
            leftIcon="communication/email"
            title={i18n.t('general.actions.sendMessage')}
            variant="link"
            size="small"
          />
        }
      >
        <Tooltip
          label={i18n.t('general.notifications.setupEmailProvider')}
          placement="top-start"
          isDisabled={!isEmailConfigEnabled}
        >
          <DropdownItem
            label={i18n.t('general.labels.email')}
            onClick={() =>
              openDialog(
                <SendCustomerEmailForm
                  customerId={props.customerId}
                  vehicleId={props.vehicleId}
                  resourceId={props.resourceId}
                  recordId={props.recordId}
                  secondaryRecordIds={props.secondaryRecordIds}
                  defaultValues={{customerEmailAddress: props.defaultEmailAddress}}
                />,
                {
                  withAdditionalFooter: true,
                  size: 'large',
                  title: i18n.t('general.actions.sendEmail'),
                }
              )
            }
            isDisabled={!isEmailConfigEnabled}
            leftIcon="communication/email"
          />
        </Tooltip>

        <Tooltip label={smsTooltipLabel} placement="bottom-start" isDisabled={!isSmsActionDisabled}>
          <DropdownItem
            label={i18n.t('general.labels.sms')}
            onClick={() => {
              if (!props.customerId) {
                return;
              }

              openDialog(
                <SendCustomerSmsForm
                  customerId={props.customerId}
                  recordId={props.recordId}
                  resourceId={props.resourceId}
                  secondaryRecordIds={props.secondaryRecordIds}
                  defaultPhoneNumber={props.defaultPhoneNumber}
                />,
                {
                  scrollBehavior: 'outside',
                  size: 'large',
                  title: i18n.t('general.labels.smsMessage'),
                }
              );
            }}
            isDisabled={isSmsActionDisabled || !props.customerId}
            leftIcon="communication/textsms"
          />
        </Tooltip>
      </Dropdown>
    </DataStatus>
  );
}
